"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SdPlatformAuthServiceApi = void 0;
const SdPlatformRequestId_1 = require("../api/SdPlatformRequestId");
const api_platform_api_dto_v1_1 = require("@shapediver/api.platform-api-dto-v1");
const jwt_decode_1 = __importDefault(require("jwt-decode"));
const SdPlatformErrorTypeGuards_1 = require("../SdPlatformErrorTypeGuards");
var RequestState;
(function (RequestState) {
    RequestState["NoRequest"] = "NoRequest";
    RequestState["Requested"] = "Requested";
    RequestState["Resolved"] = "Resolved";
    RequestState["Failed"] = "Failed";
})(RequestState || (RequestState = {}));
/**
 * Authentication service for ShapeDiver Platform.
 */
class SdPlatformAuthServiceApi {
    constructor(__context) {
        this.__context = __context;
        this.__refreshTokenFailedEvents = [];
        this.__passwordGrantFailedEvents = [];
        this.__authorizationCodeFailedEvents = [];
        /** instance of token request which is used in order to not spam with token requests */
        this.__refreshTokenRequestMeta = {
            request: undefined,
            requestState: RequestState.NoRequest,
        };
        this.__api = __context.api;
        this.__config = __context.config;
    }
    __resolveConfigAuth() {
        return {
            access_token: this.__context.accessTokenDetails.access_token,
            client_id: this.__context.config.clientId
        };
    }
    __resolveConfig() {
        return {};
    }
    __request(id, request) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.__api.post(id, "oauth/token", request, this.__resolveConfig());
        });
    }
    /**
     * Convert "expires_in" number of seconds to an absolute expiration date.
     * @param seconds
     */
    convertToExpirationDate(seconds) {
        const expiresAt = new Date();
        expiresAt.setSeconds(expiresAt.getSeconds() + seconds);
        return expiresAt;
    }
    /**
     * Set details of access token in context.
     * @param data auth response
     */
    setAccessTokenDetails(data) {
        const tokenDetails = {
            access_token: data.access_token,
            refresh_token: data.refresh_token,
            expires_in: data.expires_in,
            token_type: data.token_type,
        };
        if (tokenDetails.access_token) {
            const { sub } = this.__decodeToken(tokenDetails.access_token);
            tokenDetails.user_id = sub;
        }
        if (tokenDetails.expires_in) {
            tokenDetails.expirationDate = this.convertToExpirationDate(tokenDetails.expires_in);
        }
        this.__context.setAccessTokenDetails(tokenDetails);
    }
    get authData() {
        return this.__context.authData;
    }
    /**
     * Register callbacks for "refresh_token" grant failed events.
     * @param fn
     */
    onRefreshTokenRequestFailed(fn) {
        this.__refreshTokenFailedEvents.push(fn);
    }
    /**
     * Register callbacks for "password" grant failed events.
     * @param fn
     */
    onPasswordGrantFailed(fn) {
        this.__passwordGrantFailedEvents.push(fn);
    }
    /**
     * Register callbacks for "code" grant failed events.
     * @param fn
     */
    onAuthorizationCodeFailed(fn) {
        this.__authorizationCodeFailedEvents.push(fn);
    }
    /**
     * Try to get a fresh access token using the current refresh token. OAuth 2.0 "refresh_token" grant.
     * @returns
     */
    refreshToken() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.__refreshTokenRequestMeta.requestState == RequestState.Requested && this.__refreshTokenRequestMeta.request) {
                return this.__refreshTokenRequestMeta.request;
            }
            try {
                // TODO in case we are not running in a browser environment, 
                // the "refresh_token" cookie won't be set, and we should
                // include the refresh_token property here
                const request = this.__request(SdPlatformRequestId_1.RequestId.RefreshToken, {
                    grant_type: api_platform_api_dto_v1_1.SdPlatformAuthGrantType.RefreshToken,
                    client_id: this.__config.clientId,
                    client_secret: this.__config.clientSecret
                });
                this.__refreshTokenRequestMeta.request = request;
                this.__refreshTokenRequestMeta.requestState = RequestState.Requested;
                const data = yield request;
                this.setAccessTokenDetails(data);
                this.__refreshTokenRequestMeta.requestState = RequestState.Resolved;
                return data;
            }
            catch (error) {
                this.__refreshTokenRequestMeta.requestState = RequestState.Failed;
                if ((0, SdPlatformErrorTypeGuards_1.isPBResponseError)(error)) {
                    this.__cleanAuthData();
                    this.__refreshTokenFailedEvents.forEach(x => x(error));
                }
                throw error;
            }
        });
    }
    /**
     * Authenticate using credentials. OAuth 2.0 "password" grant.
     * @param username
     * @param password
     * @returns
     */
    passwordGrant(username, password) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const data = yield this.__request(SdPlatformRequestId_1.RequestId.PasswordGrant, {
                    grant_type: api_platform_api_dto_v1_1.SdPlatformAuthGrantType.Password,
                    client_id: this.__config.clientId,
                    username,
                    password
                });
                this.setAccessTokenDetails(data);
                return data;
            }
            catch (error) {
                if ((0, SdPlatformErrorTypeGuards_1.isPBResponseError)(error)) {
                    this.__cleanAuthData();
                    this.__passwordGrantFailedEvents.forEach(x => x(error));
                }
                throw error;
            }
        });
    }
    /**
     * Authenticate using authorization code. Open Id providers.
     * @param code
     * @param state
     * @returns
     */
    authorizationCode(code, state) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const data = yield this.__request(SdPlatformRequestId_1.RequestId.AuthorizationCode, {
                    grant_type: api_platform_api_dto_v1_1.SdPlatformAuthGrantType.Code,
                    client_id: this.__config.clientId,
                    code,
                    state,
                });
                this.setAccessTokenDetails(data);
                return data;
            }
            catch (error) {
                if ((0, SdPlatformErrorTypeGuards_1.isPBResponseError)(error)) {
                    this.__cleanAuthData();
                    this.__authorizationCodeFailedEvents.forEach(x => x(error));
                }
                throw error;
            }
        });
    }
    /**
     * Clean the authentication data, unset the "refresh_token" cookie.
     * @returns
     */
    logout() {
        const response = this.__api.post(SdPlatformRequestId_1.RequestId.Logout, "oauth/logout", {}, this.__resolveConfig());
        this.__cleanAuthData();
        return response;
    }
    /**
     * Create and get authorization code.
     * @param params
     * @returns
     */
    requestAuthorizationCode(params) {
        return this.__api.post(SdPlatformRequestId_1.RequestId.RequestAuthorizationCode, "oauth/authorize", params, Object.assign(Object.assign({}, this.__resolveConfig()), this.__resolveConfigAuth()));
    }
    __decodeToken(token) {
        return (0, jwt_decode_1.default)(token);
    }
    __cleanAuthData() {
        this.setAccessTokenDetails({});
    }
}
exports.SdPlatformAuthServiceApi = SdPlatformAuthServiceApi;
//# sourceMappingURL=SdPlatformAuthServiceApi.js.map